import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiClient from "../../../services/apiClient";

// Fetch all debts
export const fetchDebts = createAsyncThunk(
  'debt/fetchAll',
  async ({ canvasId, page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/canvas/${canvasId}/debts?page=${page}&limit=${limit}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || error);
    }
  }
);

// Fetch single debt
export const fetchDebt = createAsyncThunk(
  'debt/fetchOne',
  async ({ canvasId, debtId }, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/canvas/${canvasId}/debts/${debtId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || error);
    }
  }
);

// Create Debt
export const createDebt = createAsyncThunk(
  'debt/create',
  async ({ canvasId, debtData }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`/canvas/${canvasId}/debts`, debtData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || error);
    }
  }
);
// Update Debt
export const updateDebt = createAsyncThunk(
  'debt/update',
  async ({ canvasId, debtId, debtData }, { rejectWithValue }) => {
    try {
      const response = await apiClient.patch(`/canvas/${canvasId}/debts/${debtId}`, debtData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || error);
    }
  }
);

// Delete Debt
export const deleteDebt = createAsyncThunk(
  'debt/delete',
  async ({ canvasId, debtId }, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/canvas/${canvasId}/debts/${debtId}`);
      return debtId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || error);
    }
  }
);

// Upload Debts from file
export const uploadDebts = createAsyncThunk(
  'debt/upload',
  async ({ canvasId, file }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await apiClient.post(`/canvas/${canvasId}/debts/upload`, formData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || error);
    }
  }
);

export const debtOverview = createAsyncThunk(
  'debt/overview',
  async ({canvasId}, {rejectWithValue})=>{
    try{
    const response = await apiClient.get(`/canvas/${canvasId}/debts/overview`);
    return response.data
    }catch(e){
      return rejectWithValue(e)
    }
  }
)

export const fetchPaymentList = createAsyncThunk(
  'debt/paymentList',
  async ({canvasId, debtId}, {rejectWithValue})=>{
    try{
    const response = await apiClient.get(`/canvas/${canvasId}/debts/${debtId}/payments`);
    return response.data
    }catch(e){
      return rejectWithValue(e)
    }
  }
)
export const addPayment = createAsyncThunk(
  'debt/addPayment',
  async ({ canvasId, debtId, amount, date }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`/canvas/${canvasId}/debts/${debtId}/payments`, { amount, date });

      return { debtId, payment: response.data };

    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || error);
    }
  }
);

export const editPayment = createAsyncThunk(
  'debt/editPayment',
  async ({ canvasId, paymentId, amount, date }, { rejectWithValue }) => {
    try {
      const response = await apiClient.patch(`/canvas/${canvasId}/debts/payments/${paymentId}`, { amount, date });
      return { paymentId, payment: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || error);
    }
  }
);

export const deletePayment = createAsyncThunk(
  'debt/deletePayment',
  async ({ canvasId, paymentId }, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/canvas/${canvasId}/debts/payments/${paymentId}`);
      return { paymentId };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || error);
    }
  }
);

// Debt Slice}
const debtSlice = createSlice({
  name: "debt",
  initialState: {
    debts: [],
    paymentList: [],
    debt: null,
    loading: false,
    error: null,
    pagination: null,
    uploadResult: null,
    overview:null
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch all debts
      .addCase(fetchDebts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDebts.fulfilled, (state, action) => {
        state.loading = false;
        state.debts = action.payload.debts || action.payload.items || [];
        state.pagination = {
          total: action.payload.total,
          page: action.payload.page,
          pages: action.payload.pages,
        };
      })
      .addCase(fetchDebts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message;
      })

      // Fetch single debt
      .addCase(fetchDebt.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDebt.fulfilled, (state, action) => {
        state.loading = false;
        state.debt = action.payload;
      })
      .addCase(fetchDebt.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message;
      })

      // Create debt
      .addCase(createDebt.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createDebt.fulfilled, (state, action) => {
        state.loading = false;
        state.debts = [action.payload, ...state.debts];
      })
      .addCase(createDebt.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message;
      })

      // Update debt
      .addCase(updateDebt.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateDebt.fulfilled, (state, action) => {
        state.loading = false;
        state.debts = state.debts.map((d) =>
          d._id === action.payload._id ? action.payload : d
        );
      })
      .addCase(updateDebt.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message;
      })

      // Delete debt
      .addCase(deleteDebt.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteDebt.fulfilled, (state, action) => {
        state.loading = false;
        state.debts = state.debts.filter((d) => d._id !== action.payload);
      })
      .addCase(deleteDebt.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message;
      })

      // Upload debts
      .addCase(uploadDebts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadDebts.fulfilled, (state, action) => {
        state.loading = false;
        state.uploadResult = action.payload;
      })
      .addCase(uploadDebts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message;
      })
      .addCase(debtOverview.fulfilled, (state, action)=>{
            state.loading =false;
            state.overview = action.payload;
      })
      .addCase(fetchPaymentList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPaymentList.fulfilled, (state, action) => {
        state.loading = false;
        state.paymentList = action.payload;
      })
      .addCase(fetchPaymentList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message;
      })
      // Add Payment
      .addCase(addPayment.fulfilled, (state, action) => {
        if (state.paymentList && Array.isArray(state.paymentList)) {
          state.paymentList = [action.payload.payment, ...state.paymentList];
        }
      })
      // Edit Payment
      .addCase(editPayment.fulfilled, (state, action) => {
        if (state.paymentList && Array.isArray(state.paymentList)) {
          state.paymentList = state.paymentList.map(p =>
            p._id === action.payload.paymentId ? action.payload.payment : p
          );
        }
      })
      // Delete Payment
      .addCase(deletePayment.fulfilled, (state, action) => {
        if (state.paymentList && Array.isArray(state.paymentList)) {
          state.paymentList = state.paymentList.filter(p => p._id !== action.payload.paymentId);
        }
      });
  },
});
export const selectDebts = state => state.debt.debts;
export const selectDebtLoading = state => state.debt.loading;
export const selectDebtPagination = state => state.debt.pagination;
export const selectDebtOverview = state=>state.debt.overview;
export const selectPaymentList = state => state.debt.paymentList;
export default debtSlice.reducer;

